import GlobalNavigationBar from "@/v2-components/global-navigation-bar";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import PromoBanner from "@/components/banner/PromoBanner";
import { cn } from "@/libs/utils";
import {
  useMemo,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Product, ProductSlug } from "@/api/product-api";
import { AuthUtils } from "@/utils/AuthUtils";
import PdpCheckoutButtons from "@/v2-components/pdp-checkout-buttons";
import ScrollHideButtonByFooter from "@/v2-components/pages/tactsuit/ScrollHideButtonByFooter";
import { useScrollPast } from "@/hooks/useScrollPast";
import ReferralBanner from "@/components/banner/ReferralBanner";
import PageTemplate from "@/components/templates/PageTemplate";
import React from "react";
import TactSuitCard from "@/v2-components/TactSuitCard";
import { useBuyTactSuit } from "@/store/useBuyTactSuit";
import {
  useAddDevice,
  useCartStore,
  getCartState,
  AddToCartResult,
  useAddAccessories,
} from "@/store/cart-store";
import { useRouter } from "next/router";
import {
  useProducts,
  useGetBundleProducts,
} from "@/services/product/useProductService";
import { ProductUtil } from "@/utils/ProductUtil";
import { useLocale } from "@/store/locale-store";
import { useCurrency } from "@/hooks/useCurrency";
import { analyticsInstance } from "@/libs/analytics";
import { useFunnelValueEffect } from "@/libs/analytics/use-funnel-value-effect";
import FbPixel from "@/utils/FbPixel";
import SuggestLocale from "@/components/SuggestLocale";
import { ComponentId } from "@/constants";
import {
  useBuyTactSuitCheckout,
  useShoppingBagCheckout,
} from "@/hooks/shops/useCheckout";
import { CheckoutDialog } from "@/components/tactsuit/components/buy-tactsuit/CheckoutDialog";
import { BundleMergedDialog } from "@/components/tactsuit/components/buy-tactsuit/bundle-merged-dialog";
import BulkOrderDialog from "@/v2-components/cart-sheet/bulk-order-dialog";
import BulkOrderInquiryDialog from "@/v2-components/bulk-order-inquiry-dialog";
import { useBundledStore } from "@/store/cart-store/useBundledStore";
import { usePricedFilter } from "@/hooks/usePricedFilter";

type Props = {
  children?: React.ReactNode;
  className?: string;
  product: Product;
  countryCode: string;
};

/**
 * 우측 buy box(페이지 쪽에서 렌더링)가 담기/체크아웃 핸들러에 접근하기 위한 컨텍스트
 */
type BuyActions = {
  onAddToCart: () => void;
  onCheckout: () => void;
  /** develop #5101 — 회원 결제(로그인 후 결제). 비회원 결제는 `onCheckout`. */
  onAccountCheckout: () => void;
  isCheckingOut: boolean;
  isSoldOut: boolean;
  /**
   * 옵션 폼·상세 콘텐츠 기준 제품. 번들 업그레이드까지만 반영한다.
   * ⚠️ 값 표시(가격·제품명·재고·배송일)와 결제 payload 에는 쓰지 말 것 —
   *    `checkoutProduct` 를 써야 한다.
   */
  displayProduct: Product;
  /**
   * **선택된 SKU 를 실제로 소유한 제품** — 표시가와 청구가를 맞추는 기준.
   *
   * 사이즈 세트(`all-size`)를 고르면 SKU 가 `tactglove-dk3-bundle-…` 로 별도 제품
   * 소속이 되고 카트도 그 번들을 담는다(`makeCartItem` → `getProductBySku`).
   * `displayProduct` 는 **번들 업그레이드만** 반영하므로 세트에서는 단품을 가리킨다.
   *
   * 이 값을 컨텍스트에 두는 이유: 이 판정이 필요한 표면이 최소 4곳(본문 BuyBox,
   * 컴팩트 바, 모바일 하단 고정 바, 네이버페이)이라 한 곳에서만 고치면 **같은 화면에
   * 두 가격이 뜬다**(실측: 모바일에서 본문 $1,292 / 하단 바 $349, 담기 버튼은 하단 바).
   */
  checkoutProduct: Product;
  /**
   * **지금 화면에 떠 있는 액세서리의 SKU 집합.** 결제 경로가 이걸로 거른다.
   *
   * `checkoutProduct` 와 같은 이유로 컨텍스트에 둔다 — 결제 경로가 셋이라
   * (`addAccessoriesToCart` · `useBuyTactSuitCheckout` · `ProductNaverPayButton`)
   * 각자 계산하면 갈린다.
   *
   * ⚠️ 기준은 **실효 제품**(camo 반영된 기본 라인)이다. `checkoutProduct` 로 계산하면
   * 안 된다 — 번들일 수 있고, 그러면 `accessoryFor` 가 안 걸려 집합이 비어
   * **정당한 액세서리까지 결제에서 빠진다.**
   */
  offeredAccessorySkus: Set<string>;
  /**
   * **레일 CTA(Add to Bag/Checkout) 바로 뒤**에 놓는 높이 0 sentinel 의 ref.
   * 페이지가 붙인다.
   *
   * ⚠️ **구매 섹션 전체가 아니라 CTA 를 관측한다.** 스펙은 "Add to Bag, Checkout
   * **버튼이** 시야에서 밀리는 시점부터 sticky"인데, `#buy-section`(갤러리+레일+
   * 아코디언 전부)을 관측하면 그 요소가 통째로 화면을 벗어날 때까지 기다리게 된다.
   * 실측: 모바일에서 CTA 가 가려진 뒤 sticky 가 뜰 때까지 **2~4화면(1,275~2,537px)
   * 동안 화면에 CTA 가 0개**였다(아코디언을 열수록 더 벌어진다 — 액세서리를 담으려는
   * 행동이 자기 공백을 늘린다). 데스크톱도 같은 이유로 약 1.3화면 공백이었다.
   *
   * 소유를 템플릿으로 둔 이유: 이 신호를 **두 표면이 같이** 쓴다 — 데스크톱 상단
   * `CompactBuyBar`(페이지가 렌더)와 모바일 하단 sticky(템플릿이 렌더).
   * 각자 계산하면 "트리거를 80px 앞당기자" 같은 조정이 한쪽에만 반영된다.
   * (`checkoutProduct`·`offeredAccessorySkus` 를 여기 둔 것과 같은 이유)
   */
  buyCtaSentinelRef: React.RefObject<HTMLDivElement | null>;
  /** 레일 CTA 가 화면 위로 빠져나갔는지 — sticky CTA 노출 조건. */
  scrolledPastBuyCta: boolean;
};

const BuyActionsContext = createContext<BuyActions | null>(null);

export const useBuyActions = () => {
  const context = useContext(BuyActionsContext);
  if (!context) {
    throw new Error(
      "useBuyActions must be used within BuyTactsuitDetailPageTemplate",
    );
  }
  return context;
};

export default function BuyTactsuitDetailPageTemplate({
  children,
  className = "min-h-screen w-full",
  product,
  countryCode,
}: Props) {
  const locale = useLocale();
  const canSuggeset = !!countryCode && locale.countryCode !== countryCode;
  const currency = useCurrency();

  /**
   * ★**제품 PDP 의 `view_item` / Meta `ViewContent`** — 퍼널의 첫 단계다 (#5190).
   *
   * ## 재이식인 이유
   *
   * 이 두 줄은 원래 `TactsuitPageTemplate` 의 `useMount` 에 있었는데, PDP 리뉴얼
   * (#5095 · `993a827d8` · 2026-08-27)이 개별 PDP 페이지들(`starter-bundle.tsx` 등)과
   * 그 템플릿을 지우고 `[slug].tsx` + 이 템플릿으로 통합하면서 **같이 사라졌고 재이식되지
   * 않았다.** 그 뒤 전 제품 PDP 에서 `view_item` 이 0건이었다 — `begin_checkout` 은 찍히고
   * 앞단만 비어 있어 GA4 리포트에서는 "PDP 를 거치지 않는 구매"처럼 보인다.
   * `docs/pitfalls.md` #27(리뉴얼 시 이벤트가 조용히 사라진다)의 실측 사례다.
   *
   * ## `useFunnelValueEffect` 로 거는 이유 — 값 축이 첫 렌더에 확정되지 않는다
   *
   * ★초판은 `useEffect(…, [product?.slug])` 였는데, 그 effect 는 `_app` 의
   * `hydrateLocaleStore()`(부모 effect)보다 **먼저** 돌아 `countryCode` 로 URL 로케일 파생값
   * (`/en` → `US`)을 본다. 그러면 `view_item` 만 `usPrice` 티어로 나가고 `add_to_cart` 부터
   * `prices.USD` 로 바뀐다 — **통화 라벨은 같고 금액만 다르다.** prod 37/37 제품에서 두 값이
   * 다르다(2026-09-16 실측). 기전·대상 범위는 `use-funnel-value-effect.ts` docblock.
   *
   * 키가 `product.slug` 인 이유: 이 템플릿은 **같은 라우트에 머문 채 `product` 만 바뀐다** —
   * `[slug].tsx` 가 camo 색상 선택 시 `effectiveProduct` 를 교체해 넘긴다(#5068). camo 는
   * 색상 옵션이 아니라 **다른 제품**이므로(`docs/product-variant-line-policy.md`) 그 전환은
   * 새 `view_item` 이 맞다. `useMount` 였다면 변형 조회가 통째로 누락된다
   * (`docs/pitfalls.md` #4 — 같은 route 에서 props 변경 시).
   */
  useFunnelValueEffect(product?.slug, () => {
    analyticsInstance?.viewItem({
      product,
      currency,
      countryCode: locale.countryCode,
    });
    FbPixel.viewItem({ product, currency, countryCode: locale.countryCode });
  });
  const shoppingBagCheckout = useShoppingBagCheckout({
    // 이 훅의 소비처는 PDP 의 "담고 결제" 다이얼로그다 (#5292)
    entryPoint: "pdp",
    enabled: true,
  });
  const getSku = useBuyTactSuit((s) => s.getSku);
  const buyOptions = useBuyTactSuit((s) => s.options);
  const setShowValidationErrors = useBuyTactSuit(
    (s) => s.setShowValidationErrors,
  );
  const router = useRouter();
  const addDevice = useAddDevice();
  const addAccessories = useAddAccessories();
  const { data: products } = useProducts();
  const isSoldOut = ProductUtil.isProductSoldOut(
    products?.find((p) => p.slug === product.slug),
  );
  const {
    selectedProduct,
    selectedProductOptions,
    selectedGameResells,
    selectedAccessories,
    getSelectedProductSku,
    catalog,
  } = useBuyTactSuit((state) => ({
    selectedProduct: state.product!,
    selectedProductOptions: state.options,
    selectedAccessories: state.accessories,
    selectedGameResells: state.gameResells,
    getSelectedProductSku: state.getSku,
    catalog: state.products,
  }));
  /**
   * **지금 화면에 떠 있는 액세서리의 SKU 집합.** 결제 경로 전부가 이걸로 거른다.
   *
   * 스토어의 `accessories` 는 옵션/제품이 바뀌어도 자동으로 정리되지 않아, 목록에서
   * 사라진 항목이 `selected` 로 남아 그대로 청구됐다(실측: 글러브 사이즈 S→L, Air→Camo).
   * 스토어를 직접 읽는 결제 경로가 셋이라 각자 거르면 또 갈린다 —
   * `addAccessoriesToCart` · `useBuyTactSuitCheckout` · `ProductNaverPayButton`.
   *
   * ⚠️ **카탈로그는 스토어(`state.products`)에서 읽는다 — `useProducts()` 가 아니다.**
   * 화면의 액세서리 목록은 `[slug].tsx` 가 `getServerSideProps` prop 으로 그리고,
   * 그 배열이 `setProducts(products)` 로 스토어에 시딩된다. 여기서 react-query 를
   * 읽으면 **표시와 청구가 서로 다른 카탈로그**를 보게 되고, `?? []` 가 "아직 모름"을
   * "아무것도 제공 안 됨"으로 뭉개 **선택한 액세서리가 조용히 결제에서 빠진다**
   * (쿼리 실패, 또는 서버 prefetch 키와 클라 `bh_ccode` 폴백이 어긋나는 콜드 페치).
   * 과청구를 무음 미청구로 방향만 바꾸는 셈이라 더 나쁘다.
   * 네이버페이의 가격 조회(`buildAccessoryNaverPayItems`)도 이 스토어 배열을 쓰므로
   * 게이트와 가격이 같은 소스가 된다.
   *
   * ⚠️ 기준 제품은 **`product`(= 페이지의 `effectiveProduct`)** 다. 스토어의 `product`
   * (`displayProduct`)를 쓰면 안 된다 — 번들을 고르면 그건 번들 제품이라
   * `accessoryFor` 가 안 걸려 집합이 비고, **정당한 액세서리까지 결제에서 빠진다.**
   *
   * ⚠️ **이 필터가 안전한 근거는 배치다.** `state.products` 의 writer 는
   * `[slug].tsx` 의 마운트 effect 하나뿐이고, 거기서 `reset()`(= `accessories: []`)과
   * **같은 본문**에 있다. 그래서 "선택된 액세서리가 있다 ⟹ 카탈로그가 차 있다"가
   * 성립하고, 빈 카탈로그가 "제공 없음"으로 읽혀도 무음 유실이 안 난다.
   * **둘을 떼면 그 보장이 사라진다** — 카탈로그를 다른 effect 로 빼거나, 카탈로그보다
   * 먼저 accessories 를 채우는 진입점이 생기면 무음 유실이 돌아온다.
   * 아래 dev 경고가 그 순간을 알려준다(테스트로는 안 잡힌다 — 순수 함수가 아니라
   * 호출부 배선의 문제라서).
   */
  const priced = usePricedFilter();
  const offeredAccessorySkus = useMemo(
    () =>
      // ★표시(`[slug].tsx` 의 getOfferedAccessories)와 **같은 필터**를 건다 — 가격 없어 목록에서 숨긴
      //   액세서리가 청구 허용 목록에만 남으면 「표시 ≠ 청구」가 된다(이 함수 주석의 실측 2건과 같은 기전).
      ProductUtil.getOfferedAccessorySkus(
        priced(catalog),
        product.slug,
        buyOptions,
      ),
    [catalog, product.slug, buyOptions, priced],
  );
  if (
    process.env.NODE_ENV !== "production" &&
    !catalog.length &&
    selectedAccessories.some((a) => a.selected)
  ) {
    console.warn(
      "[buy-rail] 카탈로그가 빈 채로 선택된 액세서리가 있다 — 결제에서 무음으로 빠진다. " +
        "`setProducts` 가 `reset()` 과 같은 effect 에 있는지 확인할 것.",
    );
  }
  // ⚠️ `offeredAccessorySkus` 를 인자로 받으므로 그 선언 **뒤**에 있어야 한다.
  const buyTactSuitCheckout = useBuyTactSuitCheckout(
    offeredAccessorySkus,
    "pdp",
  );

  // 레일 CTA 가 화면 위로 밀렸는지 — 데스크톱 상단 바와 모바일 하단 바가 **같은 값**을 쓴다.
  // ref 는 페이지가 CTA 직후 sentinel 에 붙이지만, 판정은 여기서 한 번만 한다.
  const buyCtaSentinelRef = useRef<HTMLDivElement | null>(null);
  const scrolledPastBuyCta = useScrollPast(buyCtaSentinelRef);

  const [open, setOpen] = useState<AddToCartResult | null | "checkOut">(null);
  // 인라인 번들 업그레이드 선택 시 상단 바에 선택된 번들 표시 (PDP 리뉴얼)
  const bundleUpgrades = useGetBundleProducts(product);
  const displayProduct =
    selectedProduct &&
    bundleUpgrades.some((b) => b.slug === selectedProduct.slug)
      ? selectedProduct
      : product;
  // 선택된 SKU 를 소유한 제품. 판정 기준은 카트가 쓰는 것과 동일
  // (`makeCartItem` 의 `ProductUtil.getProductBySku`) — 표시와 실제 담기는 물건이
  // 갈리지 않으려면 같은 함수여야 한다.
  const selectedSkuForDisplay = getSelectedProductSku();
  const checkoutProduct = useMemo(
    () =>
      (selectedSkuForDisplay
        ? ProductUtil.getProductBySku(products ?? [], selectedSkuForDisplay)
        : null) ?? displayProduct,
    [products, selectedSkuForDisplay, displayProduct],
  );
  // develop 유래: 대량주문 다이얼로그의 "문의하기" → 문의 폼. 둘 다 본문에서 쓰여 병존시킨다.
  const [inquiryOpen, setInquiryOpen] = useState(false);
  // 병합된 번들의 **slug** — 모달이 카트에서 그 항목(가격·썸네일·옵션)을 찾는다.
  // 종전엔 이름만 필요했다(`useBundledProductName`).
  const bundledSlug = useBundledStore((s) => s.bundle);
  const { deviceItems, gameItems, showCart } = useCartStore((s) => ({
    deviceItems: s.items,
    gameItems: s.gameItems,
    showCart: s.showCart,
  }));

  // ⚠️ `trigger` 를 **인자로 받는다**(#5292). 이 헬퍼를 네 곳이 공유하는데
  // (`Add to Bag` · `Checkout` · `Account Checkout` · 결제 다이얼로그의 "담고 결제")
  // `add_to_cart` 는 버튼이 아니라 **카트 변경 감지**에서 나가 호출자를 모른다.
  // 여기서 안 흘리면 네 경로가 전부 `unknown` 으로 합쳐진다.
  const addToCart = (trigger: CartTrigger) => {
    addAccessoriesToCart(trigger);
    const cartItem = makeCartItem();
    if (!cartItem) return;
    return addDevice(cartItem, trigger);
  };

  const makeCartItem = () => {
    const selectedProductSku = getSelectedProductSku();
    if (!selectedProduct || !selectedProductSku) return;

    const productBySku = ProductUtil.getProductBySku(
      products ?? [],
      selectedProductSku,
    );

    const device = {
      slug: productBySku?.slug ?? selectedProduct.slug,
      sku: selectedProductSku,
      quantity: 1,
      options: selectedProductOptions,
    };
    return device;
  };

  const addAccessoriesToCart = (trigger: CartTrigger) => {
    const accessories = selectedAccessories
      .filter((acc) =>
        ProductUtil.isChargeableAccessory(acc, offeredAccessorySkus),
      )
      .map((acc) => ({
        slug: acc.slug as ProductSlug,
        sku: acc.sku,
        quantity: acc.quantity,
        options: [],
      }));
    if (accessories.length > 0) {
      addAccessories(accessories, trigger);
    }
  };

  const scrollIntoView = (id: string) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const getGamesMissing = (): {
    isMissing: boolean;
    index: number;
  } => {
    if (!selectedGameResells || selectedGameResells.length === 0)
      return {
        isMissing: false,
        index: -1,
      };
    const index = selectedGameResells.findIndex((r) => !r?.id);
    if (index === -1)
      return {
        isMissing: false,
        index,
      };
    return {
      isMissing: true,
      index,
    };
  };

  const handleAddToCart = () => {
    const sku = getSku();
    if (!sku) {
      validateSku();
      return;
    }

    const result = addToCart("add_to_bag");
    if (!result) return;

    switch (result) {
      case AddToCartResult.Success:
        // ★**메인 제품**은 담으면 카트 페이지로 이동한다 (#5366).
        //
        // #5355 에서 이 자리를 담기 확인 모달로 바꿨는데(#5359), 기획이 **메인 제품은 종전
        // 이동을 유지**하고 확인 팝은 **액세서리 전용**으로 정정했다(이슈 #5366 본문).
        // 담기 확인 팝의 소비처는 `useAddItemToCart` 로 옮겼다 — 그쪽이 액세서리 카드
        // (`NotifyOrAddButton`)의 담기 경로이고, 종전에 **무음**이던 자리다.
        router.push("/cart");
        break;
      case AddToCartResult.MergedToBundle:
        setOpen(AddToCartResult.MergedToBundle);
        break;
      case AddToCartResult.OverLimit:
        setOpen(AddToCartResult.OverLimit);
        break;
    }
  };

  const validateSku = () => {
    // 필수 옵션 미선택 → 섹션 wrapper 연한 red 배경 표시 (red text 는 기존대로 항상 노출).
    setShowValidationErrors(true);
    const gamesMissing = getGamesMissing();
    if (gamesMissing.isMissing) {
      scrollIntoView(
        ComponentId.BUY_TACTSUIT_GAMEKEY_OPTION.replace(
          "{index}",
          gamesMissing.index.toString(),
        ),
      );
      return;
    }
    const types = selectedProductOptions.map((o) => o.type);
    if (!types.some((t) => "color" === t)) {
      scrollIntoView(ComponentId.BUY_TACTSUIT_COLOR_OPTION);
    } else if (!types.some((t) => "size" === t)) {
      scrollIntoView(ComponentId.BUY_TACTSUIT_SIZE_OPTION);
    }
  };

  const handleCheckout = () => {
    const sku = getSku();
    if (!sku) {
      validateSku();
      return;
    }

    const total = deviceItems.length + gameItems.length;
    // 제품이 품절인 경우 추가 없이 체크아웃을 시도 합니다.
    if (isSoldOut) {
      // 비어 있는 경우
      if (!total) {
        showCart();
        return;
      }
      shoppingBagCheckout.startCheckout();
      return;
    }
    // 쇼핑백에 제품이 없는 경우 추가 후 체크아웃 — 방금 담은 구성 = 결제 구성이라 GE 표식은 카트 결제(`direct:false`)로
    // 남긴다. 바로구매로 남기면 결제 뒤 산 물건이 카트에 그대로 남는다(docs/global-e-storefront.md §4.7.3).
    if (!total) {
      addToCart("pdp_checkout");
      buyTactSuitCheckout.startCheckout({ direct: false });
      return;
    }

    setOpen("checkOut");
  };

  // 회원 결제: 고른 구성을 장바구니에 먼저 담고 로그인으로 보낸다.
  // PDP 옵션 스토어(useBuyTactSuit)는 persist 가 꺼져 있어 로그인 왕복 시 선택이 날아가지만,
  // 장바구니 스토어는 persist 라서 담아두면 구성이 유지된다. 로그인 후에는 장바구니가 열린
  // 상태로 복귀시켜 그대로 결제를 이어가게 한다.
  const handleAccountCheckout = () => {
    const sku = getSku();
    if (!sku) {
      validateSku();
      return;
    }

    const result = addToCart("pdp_account_checkout");
    if (!result) return;

    switch (result) {
      case AddToCartResult.Success:
        // 로그인 뒤 **카트로 직행**한다 (#5701). 종전엔 `/shop/buy-tactsuit?openCart=true`
        // 로 보냈는데 그 화면이 `showCart()` 로 다시 `/cart` 로 튕겨 **한 홉이 남았고**,
        // 그 인덱스는 이 복귀 외에 도달 경로가 없었다(그래서 이 PR 이 그 페이지를 지운다).
        //
        // ⚠️ 「success-url 은 파라미터 1개까지만 안전」이라는 종전 제약은 **없어졌다**
        //    (#5560 · `generateLoginLink` KDoc). 여기서 파라미터가 0인 것은 그 제약 때문이
        //    아니라 `/cart` 에 필요한 게 없어서다.
        router.push(AuthUtils.getLoginUrl("/cart"));
        break;
      // 담기는 됐지만 안내가 필요한 경우엔 로그인으로 보내지 않고 안내를 띄운다.
      case AddToCartResult.MergedToBundle:
        setOpen(AddToCartResult.MergedToBundle);
        break;
      // ★한도는 **담기의 제약이지 인증의 제약이 아니다.**
      //
      // 담기 선행의 목적은 "로그인 왕복에 PDP 선택이 날아가니 카트에 보존" 인데, 한도에
      // 걸리면 **애초에 담을 게 없으므로 보존할 것도 없다.** 여기서 안내만 띄우면 같은 SKU 가
      // 4개인 사용자는 **PDP 에서 영영 로그인할 수 없다**(막다른 길). 담기 없이 그대로
      // 로그인으로 보낸다 — 복귀 후 카트에서 수량을 조정하면 된다.
      //
      // ⚠️ `MergedToBundle` 은 성격이 다르다 — **담기는 되는데 안내가 필요한** 경우다.
      //    `OverLimit` 만 담기가 실패한다.
      //    담기 핸들러(위 switch)의 `OverLimit` 은 그대로 안내를 띄운다 — 거기서는
      //    사용자가 요청한 행위가 담기 자체이므로 안내가 맞다.
      case AddToCartResult.OverLimit:
        router.push(AuthUtils.getLoginUrl("/cart"));
        break;
    }
  };

  return (
    <>
      {open === AddToCartResult.OverLimit && (
        <BulkOrderDialog
          open={true}
          onOpenChange={() => setOpen(null)}
          onContactClick={() => setInquiryOpen(true)}
        />
      )}
      {inquiryOpen && (
        <BulkOrderInquiryDialog
          open
          onOpenChange={setInquiryOpen}
          desiredProduct={
            buyOptions.length > 0
              ? `${product.name} (${buyOptions.map((o) => `${o.type}: ${o.value}`).join(", ")})`
              : product.name
          }
        />
      )}
      {/* 번들 병합 모달 (#5356). 닫아도 **PDP 에 머무른다** — 카트로 가는 것은 사용자가
          `View Bag` 를 눌렀을 때뿐이다(종전엔 `BundledDialog` 를 닫으면 카트로 튀었다).
          담기 계열의 다른 두 갈래(성공 · 글러브 "그래도 담기")는 #5366 이 `/cart` 자동
          이동으로 되돌렸다 — 그래서 이 파일의 `/cart` push 는 3곳(자동 2 + `View Bag` 1)이다.
          ⚠️ 총량만 세면 어느 자리가 자동인지 못 가른다 — 자리별 동작은
             `added-to-bag-trigger.test.ts` 가 본다.
          (#5356 때 적은 「세 갈래 모두 PDP 에 머무르고 push 총량은 2」는 #5366 이 두 갈래를
          되돌린 뒤로 낡은 서술이었다.) */}
      {open === AddToCartResult.MergedToBundle && (
        <BundleMergedDialog
          open
          bundleSlug={bundledSlug}
          onClose={() => setOpen(null)}
          onViewCart={() => {
            setOpen(null);
            router.push("/cart");
          }}
        />
      )}
      {open === "checkOut" && (
        <CheckoutDialog
          onCheckoutWithAdding={() => {
            addToCart("pdp_checkout");
            shoppingBagCheckout.startCheckout();
            setOpen(null);
          }}
          onCheckoutWithoutAdding={() => {
            buyTactSuitCheckout.startCheckout();
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
      <PageTemplate>
        <PageTemplate.Content
          useCustomNav={true}
          className={cn("min-h-screen w-full", className)}
        >
          {/* id 는 CompactBuyBar(= [slug].tsx)가 높이를 실측해 그 아래에 붙기 위한 앵커다.
              배너(SuggestLocale·ReferralBanner·PromoBanner) 유무로 높이가 달라져서
              고정 offset 을 쓰면 조건에 따라 헤더를 덮거나 뜬다. */}
          <div id="buy-header-stack" className="sticky top-0 z-30 w-full">
            {canSuggeset && <SuggestLocale countryCode={countryCode} />}
            <ReferralBanner />
            <GlobalNavigationBar />
            <PromoBanner />
          </div>
          <BuyActionsContext.Provider
            value={{
              onAddToCart: handleAddToCart,
              onCheckout: handleCheckout,
              onAccountCheckout: handleAccountCheckout,
              isCheckingOut:
                shoppingBagCheckout.isCheckingOut ||
                buyTactSuitCheckout.isCheckingOut,
              isSoldOut,
              displayProduct,
              checkoutProduct,
              offeredAccessorySkus,
              buyCtaSentinelRef,
              scrolledPastBuyCta,
            }}
          >
            {children}
          </BuyActionsContext.Provider>
          {/* 구매 섹션이 화면 위로 밀린 뒤부터 노출 — 데스크톱 `CompactBuyBar` 와 같은 신호. */}
          <ScrollHideButtonByFooter show={scrolledPastBuyCta}>
            <TactsuitButtons
              isSoldOut={isSoldOut}
              // ⚠️ 가격·제품명·재고·배송일과 네이버페이 payload 를 그리는 바라
              // `displayProduct` 가 아니라 `checkoutProduct` 여야 한다.
              product={checkoutProduct}
              // 액세서리 집합만은 실효 제품 기준(위 타입 주석 참고).
              offeredAccessorySkus={offeredAccessorySkus}
              // 패딩은 컴포넌트가 피그마 스펙으로 갖는다(pt12 pb20 px16) — 여기선 폭만.
              className={"flex-1"}
              onAddToCart={handleAddToCart}
              onCheckout={handleCheckout}
              onAccountCheckout={handleAccountCheckout}
              isCheckingOut={
                shoppingBagCheckout.isCheckingOut ||
                buyTactSuitCheckout.isCheckingOut
              }
            />
          </ScrollHideButtonByFooter>
        </PageTemplate.Content>
      </PageTemplate>
    </>
  );
}

const TactsuitButtons = ({
  product,
  offeredAccessorySkus,
  className,
  onAddToCart,
  onCheckout,
  onAccountCheckout,
  isCheckingOut,
  isSoldOut,
}: {
  isSoldOut: boolean;
  product: Product;
  /** 화면에 떠 있는 액세서리 SKU 집합 — 네이버페이가 이걸로 거른다. */
  offeredAccessorySkus: Set<string>;
  className?: string;
  onAddToCart: () => void;
  onCheckout: () => void;
  onAccountCheckout: () => void;
  isCheckingOut?: boolean;
}) => {
  const getSku = useBuyTactSuit((s) => s.getSku);

  return (
    <>
      {/* 컨테이너 — 피그마 28718:165028 `Sticky - Bottom (Mobile)`: pt12 pb20 px16, gap16.
          ⚠️ 이 컴포넌트는 `ScrollHideButtonByFooter`(laptop:hidden) 안에서만 쓰여
          **모바일 전용**이다 — `laptop:*` variant 를 달아도 죽은 코드가 된다. */}
      <div className={cn("bg-white px-4 pb-5 pt-3", className)}>
        <TactSuitCard product={product} sku={getSku() ?? undefined}>
          <div className="flex flex-col gap-4">
            {/* 제목 행 — 제품명(좌) ↔ 가격(우). 피그마 28718:165029.
                ⚠️ 예전엔 여기에 **배송예정일 + 재고**가 있었다. 피그마는 제품명만 둔다 —
                구매 영역이 화면 밖으로 밀렸을 때 "무엇을 사는지"를 다시 알려주는 자리이고,
                배송일은 위 레일에 이미 있다. */}
            <div className="flex w-full items-center justify-between gap-x-4">
              {/* 14/18 w500 ls.1 (`text-base` = 14/18).
                  ⚠️ `min-w-0 truncate` 필수 — 우측 가격이 `shrink-0` 라 오버플로를
                  **이름이 100% 흡수**한다. 실측: KRW + 할인배지(222px) + 긴 번들명
                  ("FPS Bundle + TactVisor")이면 이름 칸이 90px 로 눌려 2줄이 되고,
                  더 긴 카피(de/ja)면 3줄까지 가며 고정 바 높이 자체가 늘어난다. */}
              <TactSuitCard.Name className="min-w-0 truncate text-base font-500 tracking-[0.1px]" />
              {/* 배지 + 판매가(14/18 w600 ls.25) + 정가(취소선). PriceDetail 이 이미 이 구성이다. */}
              <TactSuitCard.PriceDetail className="shrink-0 justify-end text-base tracking-[0.25px]" />
            </div>
            {/* ★버튼 행 — gap 8. 결제 버튼은 **하나**(`Checkout`)이고, 로그아웃이면
                  누를 때 로그인/게스트 선택 모달이 뜬다(#5293, 시안 29435:37715).
                  종전에는 비로그인에 결제가 둘로 갈려(#5101) `Add to Bag` 이 40px
                  아이콘으로 줄었는데, 이제 라벨을 유지한다 — 스티키 바에는 리테일러
                  아이콘을 두지 않는다(공간이 좁고 시안에도 없다). */}
            {/* ★두 버튼은 **5:5** 다 (시안 29444:145342 모바일 = 160+160, gap 8).
                ⚠️ **`flex-1` 로는 반반이 안 된다 — `grid-cols-2` 여야 한다.** 실측(390px,
                `/shop/buy-tactsuit/tactsuit-pro/`): 둘 다 `flex: 1 1 0%` · `min-width: 0`
                인데 **199px / 151px** 이었다(합 350 + gap 8 = 358).
                기전은 `box-sizing: border-box` + `flex-basis: 0` 이다 — border-box 에서
                flex base size 는 **padding 아래로 내려가지 못한다.** 왼쪽은 `Button` 이라
                `px-6`(48) 이 base 가 되고 오른쪽 wrapper 는 0 → 남은 302 를 반씩(151) 나눠
                48+151 / 0+151 이 된다. `min-w-0` 은 이 축과 무관해서 안 듣는다(붙여서 재봤다).
                `grid-cols-2` 는 트랙 폭을 **컨테이너에서** 계산하므로 아이템 padding 과
                무관하게 정확히 반이다.
                ⚠️ 반으로 줄면 라벨이 넘치므로 **줄바꿈을 허용**한다(`Button` base 가
                `whitespace-nowrap`). 최장은 es `Add to Bag` = "Agregar al carrito" 18자 ·
                fr "Ajouter au panier" 17자다. 자르지 않는 이유는 무엇을 담는지가 사라지기
                때문이다. */}
            {/* ★★트랙 개수는 **버튼 개수**가 정한다 — 품절이면 결제 버튼이 없어 자식이
                하나다. `grid-cols-2` 로 고정하면 `Notify me` 가 **첫 칸(175px)만** 차지해
                반쪽이 된다. 실측: 컨테이너 358 · 자식 1개 · `grid-cols-2` → **175px**.
                (종전 `flex` + `flex-1` 은 자식이 하나면 전폭이라 이 문제가 없었다 —
                `grid` 로 바꾸면서 **새로 생긴** 축이므로 같이 잠근다.)
                같은 결함을 카트 회원 분기에서 먼저 밟았다(#5382, KR 회원 `결제하기` 반쪽). */}
            <div
              className={cn(
                "grid w-full items-stretch gap-2",
                isSoldOut ? "grid-cols-1" : "grid-cols-2",
              )}
            >
              <TactSuitCard.NotifyOrAddToBag
                className="h-10 min-w-0 whitespace-normal leading-4"
                addToCart={onAddToCart}
                disabled={isCheckingOut}
              />
              {!isSoldOut && (
                // develop #5101 — 비회원/회원 결제 분리. 우리 브랜치가 상단 `StatusBar` 를
                // 통째로 걷어내면서 이 버튼이 갈 곳을 잃었는데, 그대로 두면 PDP 만 단일
                // '결제하기' 로 되돌아간다(pitfall #27: 표면을 갈아치우면 기능이 조용히 사라진다).
                // 로그인 상태·인증 로딩 중에는 컴포넌트가 알아서 버튼 하나로 그린다.
                <PdpCheckoutButtons
                  className="h-10 min-w-0"
                  isCheckingOut={isCheckingOut}
                  onGuestCheckout={onCheckout}
                  onAccountCheckout={onAccountCheckout}
                />
              )}
            </div>
            {/* ★모바일 스티키 바에는 네이버페이를 **그리지 않는다** (#5378).
                  시안 `버튼정책`(29444:145342)의 모바일 3케이스(재고있음·혜택바·품절)는
                  `Payments` 가 **Row 하나 — 버튼 2개(160+160)** 뿐이다. 데스크톱에만
                  `Express checkout` 구분선 + `NPAY 구매 버튼 영역`(464×40)이 있다.

                  데스크톱 사본은 **별도로 살아 있다** — `[slug].tsx` 의 메인 레일.
                  #5081 때 그 자리가 비어 **데스크톱 KR 사용자에게 네이버페이가 아예 안
                  보였던** 이력이 있으므로 그쪽을 지우면 그 회귀가 재발한다.

                  ⚠️ `hidden` 으로 감추는 방식은 쓰지 않는다. 네이버페이 SDK 는 dispose 가
                  비공개라 DOM 에 남으면 3초 `setInterval`(혜택 문구 회전)과 resize 리스너가
                  계속 돌고, 이 바의 `inert` 밖이라 포커스도 잡힌다
                  (`ScrollHideButtonByFooter.tsx` 의 언마운트 금지 주석과 같은 근거).
                **처음부터 마운트하지 않으면** 토글이 없어 그 누수 조건이 성립하지 않는다.

                ⚠️ 종전에는 이 버튼 행이 `flex flex-col gap-3` wrapper 안에 있었다. 그
                wrapper 의 존재 이유가 **네이버페이와의 세로 간격**이었고, 그게 사라져
                자식이 하나뿐이 됐다 — `gap` 이 무효인 죽은 레이아웃 + 스테일 근거라
                걷었다(리뷰 #5379 Nit 1). 부모(`gap-4`)가 직접 이 행을 감싼다. */}
          </div>
        </TactSuitCard>
      </div>
    </>
  );
};
